import {
  ChargebeeSubscriptionStatus,
  Feature,
  FeatureType,
  type FrontendPermissionType,
  Institution,
  Plan,
  parseChargebeeFeatureValue,
} from '@faceup/utils'
import { createContext, type ReactNode, useContext } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import type { UpsellData } from '../components'

const fragments = {
  InstitutionProvider_company: graphql(`
    fragment InstitutionProvider_company on Company {
      id
      organizationalUnitName
      type
      country
      realInstitution
      countOfInstitutionMembers(motherId: $motherId)
      countOfForms
      hasEmployees
      config {
        id
        institutionName
      }
      billing {
        id
        plan
        employees
        subscriptionStatus
        entitlements {
          id
          featureId
          enabled
          value
          type
        }
      }
    }
  `),
  InstitutionProvider_member: graphql(`
    fragment InstitutionProvider_member on Member {
      id
      keys {
        id
        permissions(motherId: $motherId) {
          id
          type
          enabled
          additionalData {
            id
            categoryIds
          }
        }
      }
    }
  `),
}

type InstitutionContextProps = {
  data: {
    usePaidVersion: boolean
    plan: Plan
    institution: Institution
    name: string
    membersCount: number
    formsCount: number
    entitlements: {
      id: Feature
      isEnabled: boolean
      value: ReturnType<typeof parseChargebeeFeatureValue>
    }[]
    hasEmployees: boolean
    isRealInstitution: boolean
    employees: number | null
    subscriptionStatus: ChargebeeSubscriptionStatus | null
    permissions: {
      id: string
      type: FrontendPermissionType
      enabled: boolean
      additionalData: {
        id: string
        categoryIds: string[] | null
      } | null
    }[]
  }
  upsell:
    | {
        isAllowed: false
      }
    | {
        isAllowed: true
        buttons: (upsell: UpsellData) => ReactNode
      }
}

type InstitutionProviderProps = {
  children: ReactNode
  institution: FragmentType<typeof fragments.InstitutionProvider_company>
  member: FragmentType<typeof fragments.InstitutionProvider_member>
} & Pick<InstitutionContextProps, 'upsell'>

const InstitutionContext = createContext<InstitutionContextProps | undefined>(undefined)

export const InstitutionProvider = ({
  children,
  institution: _institution,
  member: _member,
  ...props
}: InstitutionProviderProps) => {
  const institution = getFragmentData(fragments.InstitutionProvider_company, _institution)
  const member = getFragmentData(fragments.InstitutionProvider_member, _member)
  const billing = institution.billing
  const entitlements = billing?.entitlements
  if (billing === null || entitlements === undefined || institution.realInstitution === null) {
    throw new Error('Institution info is not loaded properly')
  }
  const data: InstitutionContextProps['data'] = {
    usePaidVersion: billing.plan !== Plan.Free,
    plan: billing.plan,
    institution: institution.type,
    membersCount: institution.countOfInstitutionMembers,
    formsCount: institution.countOfForms,
    hasEmployees: institution.hasEmployees,
    name: institution.config.institutionName,
    isRealInstitution: institution.realInstitution,
    employees: billing.employees,
    subscriptionStatus: billing.subscriptionStatus,
    entitlements: entitlements.map(feature => ({
      id: Feature[feature.featureId],
      isEnabled: feature.enabled,
      value: parseChargebeeFeatureValue(FeatureType[feature.type], feature.value),
    })),
    permissions: member.keys.permissions,
  }
  return (
    <InstitutionContext.Provider value={{ ...props, data }}>{children}</InstitutionContext.Provider>
  )
}

type UseInstitutionProps = {
  __unsafeSkipCheckIfNotWrappedInInstitutionProvider?: boolean
}

export const useInstitution = (props?: UseInstitutionProps) => {
  const context = useContext(InstitutionContext)
  if (!context) {
    // Workaround for kredenc due to use of useAccessRights in CreateMemberModal
    if (props?.__unsafeSkipCheckIfNotWrappedInInstitutionProvider) {
      return {
        data: {
          usePaidVersion: false,
          plan: Plan.Free,
          institution: Institution.Company,
          name: '',
          membersCount: 0,
          formsCount: 0,
          entitlements: [],
          hasEmployees: false,
          isRealInstitution: false,
          employees: 0,
          subscriptionStatus: ChargebeeSubscriptionStatus.Cancelled,
          permissions: [],
        },
        upsell: {
          isAllowed: false,
        },
      }
    }
    throw new Error('useInstitution must be used within an InstitutionProvider')
  }
  return context
}
